import type { ChannelResource, GetTenantedProjectDashboardBffResponse, LifecycleResource, OverviewReleaseBff, ProjectResource, TenantedProjectDashboardFilter } from "@octopusdeploy/octopus-server-client";
import { Repository, Permission } from "@octopusdeploy/octopus-server-client";
import { uniq } from "lodash";
import { useRef, useCallback, useMemo } from "react";
import type { RenderDashboardProps } from "~/areas/dashboard/DashboardOverview/DashboardOverviewPage";
import { dashboardMissingVariablesRefreshIntervalInMs, dashboardRefreshIntervalInMs } from "~/areas/dashboard/DashboardOverview/DashboardOverviewPage";
import DashboardDataCube from "~/areas/projects/components/DashboardDataSource/DashboardDataCube";
import type { DataCube } from "~/areas/projects/components/DashboardDataSource/DataCube";
import { DimensionTypes } from "~/areas/projects/components/DashboardDataSource/DataCube";
import type { ProjectDashboardPageFilters } from "~/areas/projects/components/ProjectDashboardPage/hooks/useProjectDashboardFilters";
import { repository } from "~/clientInstance";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import { useDoBusyTaskEffect } from "~/components/DataBaseComponent/index";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import * as tenantTagsets from "~/components/tagSets";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { useRefreshLoop } from "~/hooks/useRefreshLoop";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import { useEphemeralEnvironmentsEnabled } from "../EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { getLiveStatus } from "./liveStatusLoader";
interface TenantedProjectDashboardDataSourceProps {
    project: ProjectResource;
    filters: ProjectDashboardPageFilters;
    render: (props: RenderDashboardProps) => JSX.Element;
    doBusyTask: DoBusyTask;
    cube: DataCube | undefined;
    onCubeLoaded: (cube: DataCube, totalItems: number, filters: ProjectDashboardPageFilters) => void;
    isPaged: boolean;
    showLiveStatus: boolean;
}
export default function TenantedProjectDashboardDataSource({ doBusyTask, filters, project, render, onCubeLoaded, cube, isPaged, showLiveStatus }: TenantedProjectDashboardDataSourceProps) {
    const isGrpcHaEnabled = useOctopusFeatureToggle("grpc-high-availability", false);
    const ephemeralEnvironmentsEnabled = useEphemeralEnvironmentsEnabled();
    const initialMissingVariables = useMemo(() => listTenantsWithMissingVariables(project), [project]);
    const missingVariableTenantsPromiseRef = useRef(initialMissingVariables);
    const refreshMissingVariables = useCallback(async () => {
        missingVariableTenantsPromiseRef.current = listTenantsWithMissingVariables(project);
    }, [project]);
    const dashboardRequest = useDoBusyTaskEffect(doBusyTask, async () => {
        const pageSize = isPaged ? filters.pageSize : Repository.takeAll;
        const { cube, totalItems } = await tenantedProjectDashboardRefresh(project, filters, (filters.page - 1) * pageSize, pageSize, isGrpcHaEnabled, ephemeralEnvironmentsEnabled, missingVariableTenantsPromiseRef.current);
        onCubeLoaded(cube, totalItems, filters);
    }, [filters, project], { timeOperationOptions: timeOperationOptions.forRefresh() });
    useRefreshLoop(dashboardRequest, dashboardRefreshIntervalInMs);
    useRefreshLoop(refreshMissingVariables, dashboardMissingVariablesRefreshIntervalInMs);
    return render({ cube, showLiveStatus });
}
async function tenantedProjectDashboardRefresh(project: ProjectResource, filters: ProjectDashboardPageFilters, skip: number, take: number, isGrpcHaEnabled: boolean, ephemeralEnvironmentsEnabled: boolean, missingVariableTenantsPromise: Promise<string[]>): Promise<{
    cube: DataCube;
    totalItems: number;
}> {
    const projectLiveStatusPromise = getLiveStatus(repository, project.Id, isGrpcHaEnabled);
    const releaseId = tryGetDeploymentOverviewFilterValues(filters, DimensionTypes.Release)?.[0];
    const releases = await loadReleases(project, releaseId);
    const channelsLoader = repository.Channels.allFromProject(project);
    const [{ Dashboard: dashboard, TotalItems: totalItems }, channels, promotions, tagSets, lifecycles] = await Promise.all([
        loadTenantedProjectDashboardData(project, filters, skip, take, releaseId),
        channelsLoader,
        releaseId ? repository.Releases.getDeploymentTemplate(releases[0]) : Promise.resolve(null),
        tenantTagsets.getAllByScopes(["Tenant"]),
        loadLifecycles(channelsLoader),
    ]);
    const deploymentFreezes = await repository.DeploymentFreezes.allActive();
    const frozenEnvironments = uniq(deploymentFreezes.DeploymentFreezes.flatMap((df) => Object.values(df.ProjectEnvironmentScope).flatMap((env) => env)));
    for (const envRefData of dashboard.Environments) {
        envRefData.IsFrozen = frozenEnvironments.indexOf(envRefData.Id) > -1;
    }
    const projectLiveStatusResponse = await projectLiveStatusPromise;
    const cube = new DashboardDataCube(dashboard, dashboard.Tenants, releases, channels.filter((channel) => !ephemeralEnvironmentsEnabled || channel.Type === "Lifecycle"), promotions, tagSets, lifecycles, missingVariableTenantsPromise, deploymentFreezes.DeploymentFreezes, [projectLiveStatusResponse.ProjectLiveStatus], project, releaseId);
    return { cube, totalItems };
}
function loadTenantedProjectDashboardData(project: ProjectResource, filters: ProjectDashboardPageFilters, skip: number, take: number, releaseId?: string): Promise<GetTenantedProjectDashboardBffResponse> {
    const selectedTenants = tryGetDeploymentOverviewFilterValues(filters, DimensionTypes.Tenant);
    const selectedTags = tryGetDeploymentOverviewFilterValues(filters, DimensionTypes.TagSet);
    const args: TenantedProjectDashboardFilter = { projectId: project.Id, showAll: true, selectedTenants, selectedTags, releaseId, skip, take };
    return repository.Dashboards.getTenantedProjectDashboard(args);
}
async function loadReleases(project: ProjectResource, releaseId?: string): Promise<OverviewReleaseBff[]> {
    if (releaseId) {
        return Promise.resolve([await repository.Releases.get(releaseId)]);
    }
    return await repository.Projects.getBffOverviewReleases(project);
}
function tryGetDeploymentOverviewFilterValues(filters: ProjectDashboardPageFilters, dimensionType: DimensionTypes) {
    return filters[dimensionType] ? Object.keys(filters[dimensionType]) : undefined;
}
async function loadLifecycles(channelsAsync: Promise<ChannelResource[]>): Promise<LifecycleResource[]> {
    const channels = await channelsAsync;
    const lifecycleIds = channels.map((channel) => channel.LifecycleId);
    const lifecycles = await repository.Lifecycles.allById(lifecycleIds);
    return Object.keys(lifecycles).map((id) => lifecycles[id]);
}
async function listTenantsWithMissingVariables(project: ProjectResource): Promise<string[]> {
    if (!isFeatureToggleEnabled("show-missing-variables-icon")) {
        return [];
    }
    const hasLibraryVariableSetView = isAllowed({ permission: Permission.LibraryVariableSetView, environment: "*", tenant: "*" });
    if (hasLibraryVariableSetView) {
        const missingVariables = await repository.Tenants.missingVariables({ projectId: project.Id }, false);
        return missingVariables.map((mv) => mv.TenantId);
    }
    return Promise.resolve([]);
}
